import { useEffect, useState } from "react";
import { fetchIDP, saveIDP, completeIDPAction, fetchRoles } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Progress } from "./ui/progress";
import { toast } from "sonner";
import { Loader2, Plus, Target, GraduationCap, Users2, Briefcase, Trash2, CheckCircle2, Save, MoveUp } from "lucide-react";

const ACTION_TYPES = [
  { value: "training", label: "Eğitim", icon: <GraduationCap className="w-3.5 h-3.5" /> },
  { value: "mentorship", label: "Mentorluk", icon: <Users2 className="w-3.5 h-3.5" /> },
  { value: "coaching", label: "Koçluk", icon: <Users2 className="w-3.5 h-3.5" /> },
  { value: "project", label: "Proje", icon: <Briefcase className="w-3.5 h-3.5" /> },
  { value: "other", label: "Diğer", icon: <Target className="w-3.5 h-3.5" /> },
];

const STATUS_LABELS = {
  planned: "Planlandı",
  in_progress: "Devam Ediyor",
  completed: "Tamamlandı",
  blocked: "Engellendi",
};

const emptyGoal = () => ({
  title: "",
  description: "",
  target_date: "",
  priority: "medium",
  status: "active",
  actions: [],
});

const emptyAction = () => ({
  type: "training",
  title: "",
  provider: "",
  status: "planned",
  target_date: "",
  completion_pct: 0,
});

export default function IDPEditor({ employeeId, currentUser = "employee" }) {
  const [idp, setIdp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    fetchRoles().then((r) => setRoles(Array.isArray(r) ? r : (r?.roles || []))).catch(() => setRoles([]));
  }, []);
  useEffect(() => {
    if (!employeeId) return;
    setLoading(true);
    fetchIDP(employeeId)
      .then((d) => { setIdp(d); setDirty(false); })
      .catch(() => setIdp({ goals: [], completion_pct: 0 }))
      .finally(() => setLoading(false));
  }, [employeeId]);

  const updateGoal = (i, patch) => {
    const goals = [...(idp?.goals || [])];
    goals[i] = { ...goals[i], ...patch };
    setIdp({ ...idp, goals });
    setDirty(true);
  };

  const addGoal = () => {
    const goals = [...(idp?.goals || []), emptyGoal()];
    setIdp({ ...idp, goals });
    setDirty(true);
  };

  const removeGoal = (i) => {
    const goals = (idp?.goals || []).filter((_, idx) => idx !== i);
    setIdp({ ...idp, goals });
    setDirty(true);
  };

  const addAction = (gi) => {
    const goals = [...(idp?.goals || [])];
    goals[gi] = { ...goals[gi], actions: [...(goals[gi].actions || []), emptyAction()] };
    setIdp({ ...idp, goals });
    setDirty(true);
  };

  const updateAction = (gi, ai, patch) => {
    const goals = [...(idp?.goals || [])];
    const actions = [...(goals[gi].actions || [])];
    actions[ai] = { ...actions[ai], ...patch };
    goals[gi] = { ...goals[gi], actions };
    setIdp({ ...idp, goals });
    setDirty(true);
  };

  const removeAction = (gi, ai) => {
    const goals = [...(idp?.goals || [])];
    goals[gi] = { ...goals[gi], actions: (goals[gi].actions || []).filter((_, idx) => idx !== ai) };
    setIdp({ ...idp, goals });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { goals: idp.goals || [], updated_by: currentUser, notes: idp.notes || "" };
      const saved = await saveIDP(employeeId, payload);
      setIdp(saved);
      setDirty(false);
      toast.success("Gelişim planı kaydedildi.");
    } catch (e) {
      toast.error("Kayıt başarısız.");
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteAction = async (actionId) => {
    try {
      const saved = await completeIDPAction(employeeId, actionId);
      setIdp(saved);
      toast.success("Aksiyon tamamlandı olarak işaretlendi.");
    } catch {
      toast.error("İşlem başarısız.");
    }
  };

  if (loading) {
    return (
      <div className="card-surface p-8 flex items-center justify-center gap-3 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> IDP yükleniyor...
      </div>
    );
  }

  const goals = idp?.goals || [];
  const completionPct = idp?.completion_pct || 0;

  return (
    <div className="space-y-5" data-testid="idp-editor">
      {/* Header */}
      <div className="card-surface p-5 flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="label-eyebrow flex items-center gap-2">
            <Target className="w-3.5 h-3.5 text-indigo-600" /> Bireysel Gelişim Planı (IDP)
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mt-1">Gelişim Hedeflerin</h2>
          <p className="text-sm text-slate-600 mt-1">
            Eğitim, mentorluk, koçluk ve proje aksiyonlarını hedeflerin altına ekleyerek gelişim yolunu tanımla.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">İlerleme</div>
            <div className="text-2xl font-bold text-indigo-600">%{completionPct}</div>
          </div>
          <div className="w-32">
            <Progress value={completionPct} className="h-2" />
          </div>
        </div>
        <div className="w-full flex justify-between items-center pt-3 border-t border-slate-100">
          <div className="text-xs text-slate-500">
            {idp?.updated_at ? `Son güncelleme: ${new Date(idp.updated_at).toLocaleString("tr-TR")} · ${idp.updated_by || "—"}` : "Henüz kaydedilmedi."}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={addGoal} data-testid="idp-add-goal">
              <Plus className="w-3.5 h-3.5 mr-1" /> Hedef Ekle
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving || !dirty} data-testid="idp-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Kaydet
            </Button>
          </div>
        </div>
      </div>

      {/* Goals list */}
      {goals.length === 0 ? (
        <div className="card-surface p-10 text-center">
          <Target className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <div className="text-sm text-slate-600">Henüz gelişim hedefin yok. &quot;Hedef Ekle&quot; ile başla.</div>
        </div>
      ) : (
        goals.map((g, gi) => (
          <div key={gi} className="card-surface p-5 space-y-3" data-testid={`idp-goal-${gi}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1 space-y-2">
                <Input
                  placeholder="Hedef başlığı (ör: 12 ay içinde Mağaza Müdür Yardımcılığına terfi)"
                  value={g.title}
                  onChange={(e) => updateGoal(gi, { title: e.target.value })}
                  className="text-base font-medium"
                  data-testid={`idp-goal-title-${gi}`}
                />
                <Textarea
                  placeholder="Açıklama, başarı kriterleri..."
                  value={g.description || ""}
                  onChange={(e) => updateGoal(gi, { description: e.target.value })}
                  rows={2}
                  className="text-sm"
                />
                <div className="flex gap-2 flex-wrap items-center text-xs">
                  <div className="flex items-center gap-1">
                    <MoveUp className="w-3 h-3 text-indigo-600" />
                    <span className="text-slate-500">Hedef Rol:</span>
                    <Select value={g.target_role_id || "__none__"} onValueChange={(v) => updateGoal(gi, { target_role_id: v === "__none__" ? null : v })}>
                      <SelectTrigger className="w-64 h-8 inline-flex bg-white" data-testid={`idp-goal-target-role-${gi}`}><SelectValue placeholder="Rol seç..." /></SelectTrigger>
                      <SelectContent className="bg-white max-h-72">
                        <SelectItem value="__none__">— Hedef rol yok —</SelectItem>
                        {roles.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.title} {r.career_band ? `(${r.career_band})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <span className="text-slate-500 mr-1">Öncelik:</span>
                    <Select value={g.priority} onValueChange={(v) => updateGoal(gi, { priority: v })}>
                      <SelectTrigger className="w-28 h-8 inline-flex bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="high">Yüksek</SelectItem>
                        <SelectItem value="medium">Orta</SelectItem>
                        <SelectItem value="low">Düşük</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-500">Hedef tarih:</span>
                    <Input type="date" value={g.target_date || ""} onChange={(e) => updateGoal(gi, { target_date: e.target.value })} className="w-40 h-8" />
                  </div>
                  <div>
                    <span className="text-slate-500 mr-1">Durum:</span>
                    <Select value={g.status || "active"} onValueChange={(v) => updateGoal(gi, { status: v })}>
                      <SelectTrigger className="w-28 h-8 inline-flex bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="active">Aktif</SelectItem>
                        <SelectItem value="completed">Tamamlandı</SelectItem>
                        <SelectItem value="paused">Duraklatıldı</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => removeGoal(gi)} data-testid={`idp-remove-goal-${gi}`}>
                <Trash2 className="w-4 h-4 text-slate-400" />
              </Button>
            </div>

            {/* Actions */}
            <div className="border-t border-slate-100 pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-medium text-slate-600 uppercase tracking-wide">Aksiyonlar ({g.actions?.length || 0})</div>
                <Button size="sm" variant="outline" onClick={() => addAction(gi)} className="h-7 text-xs" data-testid={`idp-add-action-${gi}`}>
                  <Plus className="w-3 h-3 mr-1" /> Aksiyon Ekle
                </Button>
              </div>
              <div className="space-y-2">
                {(g.actions || []).map((a, ai) => (
                  <div key={ai} className="flex items-center gap-2 p-2.5 rounded-lg bg-slate-50 border border-slate-100" data-testid={`idp-action-${gi}-${ai}`}>
                    <Select value={a.type} onValueChange={(v) => updateAction(gi, ai, { type: v })}>
                      <SelectTrigger className="w-32 h-8 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {ACTION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input
                      value={a.title}
                      onChange={(e) => updateAction(gi, ai, { title: e.target.value })}
                      placeholder="Aksiyon başlığı (ör: Enocta VM Uzmanlık Programı)"
                      className="flex-1 h-8"
                    />
                    <Input
                      value={a.provider || ""}
                      onChange={(e) => updateAction(gi, ai, { provider: e.target.value })}
                      placeholder="Sağlayıcı"
                      className="w-32 h-8"
                    />
                    <Select value={a.status} onValueChange={(v) => updateAction(gi, ai, { status: v, completion_pct: v === "completed" ? 100 : a.completion_pct })}>
                      <SelectTrigger className="w-32 h-8 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {a.status !== "completed" && a.id && (
                      <Button size="icon" variant="ghost" onClick={() => handleCompleteAction(a.id)} title="Tamamlandı olarak işaretle" data-testid={`idp-complete-action-${a.id}`}>
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => removeAction(gi, ai)} data-testid={`idp-remove-action-${gi}-${ai}`}>
                      <Trash2 className="w-3.5 h-3.5 text-slate-400" />
                    </Button>
                  </div>
                ))}
                {(g.actions || []).length === 0 && (
                  <div className="text-xs text-slate-400 italic py-2 text-center">Bu hedefe henüz aksiyon eklenmedi.</div>
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
